// Layout engine boundary: TS gathers browser/runtime leaf metrics, Rust owns
// scene traversal, frame layout, transforms, bounds, and layout maps.

import { computeSceneLayout as computeEngineSceneLayout } from 'fluveria-engine-wasm';
import type { GraphRuntimeResolver, Size, Space } from '../graph/graphRuntime.js';
import { hasContentAxis } from '../graph/graphRuntime.js';
import type { ParamStore } from '../graph/nodeDefs.js';
import { mergeParamOverrides } from '../graph/paramKeys.js';
import type { Mat4, Quad, Rect, Vec2 } from './layoutMath.js';
import type { ComponentDef, LayerNode, Scene, SceneNodeId } from './sceneState.js';

export interface MeasureResult {
  size: Size;
}

export type MeasureResultMap = Record<SceneNodeId, MeasureResult>;

export interface NodeLayout {
  fromLocal: Mat4;
  toLocal: Mat4;
  localBounds: Rect;
  quad: Quad;
  aabb: Rect;
}

export type LayoutMap = Record<SceneNodeId, NodeLayout>;

export interface LayoutResult {
  measureMap: MeasureResultMap;
  layoutMap: LayoutMap;
}

/**
 * Between a placement and the node inside it in a node's path: the engine's
 * `InstancePath` (`<instance>\u001f<node>`). A path names a node by the
 * placements it is reached through, not by the frames it stands in: what a
 * running document's behaviours key their parameters and their session by.
 */
export const INSTANCE_PATH_SEPARATOR = '\u001f';

/** The path of `id` in the scene whose nodes are named under `prefix`. */
export function nodePath(prefix: string, id: string): string {
  return `${prefix}${id}`;
}

/** The prefix the nodes inside the placement at `path` are named under. */
export function placementPrefix(path: string): string {
  return `${path}${INSTANCE_PATH_SEPARATOR}`;
}

/**
 * The session a running document is laid out and drawn in -- its behaviours'
 * (`dataRuntime`), each by a node's path: where each scrolling frame looks,
 * how much of each folding frame is shown, how each turned node is turned,
 * and whether each node a behaviour showed or hid is shown.
 */
export interface LayoutSession {
  scrollOffsets: Record<string, Vec2>;
  reveals: Record<string, number>;
  rotations: Record<string, number>;
  shown: Record<string, boolean>;
}

export interface SceneLayoutContext {
  componentLibrary: Record<string, ComponentDef>;
  params: ParamStore;
  resolveRuntime?: GraphRuntimeResolver;
  parentTransform?: Mat4;
  behaviorParams?: Record<string, ParamStore>;
  /** The prefix this scene's nodes are named under: a placement's, inside one. */
  behaviorPathPrefix?: string;
  session?: LayoutSession;
}

/** Whether the node at `path` is shown: as the session says where it says, as written elsewhere. */
export function shownInSession(session: LayoutSession | undefined, path: string, authored: boolean): boolean {
  return session?.shown[path] ?? authored;
}

export interface CoreLayerLayoutMetric {
  intrinsic: Size;
  usesLayoutBounds: boolean;
}

interface CoreNodeLayout {
  fromLocal: ArrayLike<number>;
  toLocal: ArrayLike<number>;
  localBounds: Rect;
  quad: Quad;
  aabb: Rect;
}

interface CoreLayoutResult {
  measureMap: CoreMap<SceneNodeId, MeasureResult>;
  layoutMap: CoreMap<SceneNodeId, CoreNodeLayout>;
}

type CoreMap<K extends string, V> = Map<K, V> | Record<K, V>;

/**
 * Full layout pass.
 *
 * Graph runtimes still live in TS because they depend on browser-side graph and
 * SDF caches. Core receives only their layout-facing leaf metrics.
 */
export function computeLayout(
  scene: Scene,
  rootSpace: Space,
  context: SceneLayoutContext,
): LayoutResult {
  const layerMetrics = layerLayoutMetrics(scene, rootSpace, context);
  const prefix = context.behaviorPathPrefix ?? '';
  const result = computeEngineSceneLayout({
    scene,
    rootSpace,
    componentLibrary: context.componentLibrary,
    layerMetrics,
    parentTransform: context.parentTransform ? Array.from(context.parentTransform) : undefined,
    scrollOffsets: sessionUnder(context.session?.scrollOffsets, prefix),
    reveals: sessionUnder(context.session?.reveals, prefix),
    rotations: sessionUnder(context.session?.rotations, prefix),
  }) as CoreLayoutResult;
  return normalizeCoreLayoutResult(result);
}

/**
 * What the session holds for the scene whose nodes are named under `prefix`,
 * named as that scene names them -- a frame inside a placement by its id
 * there, one inside a placement standing in it by its path from there: what
 * the engine lays an interior out by.
 */
function sessionUnder<T>(entries: Record<string, T> | undefined, prefix: string): Record<string, T> {
  const under: Record<string, T> = {};
  for (const [path, value] of Object.entries(entries ?? {})) {
    if (path.startsWith(prefix)) under[path.slice(prefix.length)] = value;
  }
  return under;
}

/** What the host measures of the scene's layers, by node id, as the engine's layout takes it. */
export function layerLayoutMetrics(
  scene: Scene,
  rootSpace: Space,
  context: SceneLayoutContext,
): Record<SceneNodeId, CoreLayerLayoutMetric> {
  const metrics: Record<SceneNodeId, CoreLayerLayoutMetric> = {};
  collectSceneLayerLayoutMetrics(
    scene,
    scene.rootIds,
    rootSpace,
    context,
    metrics,
    new Set(),
    context.behaviorPathPrefix ?? '',
  );
  return metrics;
}

function collectSceneLayerLayoutMetrics(
  scene: Scene,
  nodeIds: SceneNodeId[],
  rootSpace: Space,
  context: SceneLayoutContext,
  metrics: Record<SceneNodeId, CoreLayerLayoutMetric>,
  ancestors: Set<string>,
  pathPrefix: string,
  inheritedSerialized: Record<string, unknown> = {},
  inheritedRuntime: Record<string, unknown> = {},
): void {
  for (const nodeId of nodeIds) {
    const node = scene.nodes[nodeId];
    if (!node) continue;
    const path = nodePath(pathPrefix, node.id);
    const ownerRuntime = context.behaviorParams?.[path] ?? {};
    if (node.kind === 'layer') {
      metrics[node.id] = measureLayerForCore(node, rootSpace, context, {
        ...inheritedSerialized,
        ...inheritedRuntime,
        ...node.paramOverrides,
        ...ownerRuntime,
      });
    } else if (node.kind === 'frame') {
      collectSceneLayerLayoutMetrics(scene, node.childIds, rootSpace, context, metrics, ancestors, pathPrefix, inheritedSerialized, { ...inheritedRuntime, ...ownerRuntime });
    } else {
      collectInstanceLayerLayoutMetrics(
        node.componentId,
        rootSpace,
        context,
        metrics,
        ancestors,
        placementPrefix(path),
        { ...inheritedSerialized, ...node.paramOverrides },
        { ...inheritedRuntime, ...ownerRuntime },
      );
    }
  }
}

function collectInstanceLayerLayoutMetrics(
  componentId: string,
  rootSpace: Space,
  context: SceneLayoutContext,
  metrics: Record<SceneNodeId, CoreLayerLayoutMetric>,
  ancestors: Set<string>,
  pathPrefix: string,
  inheritedSerialized: Record<string, unknown>,
  inheritedRuntime: Record<string, unknown>,
): void {
  if (ancestors.has(componentId)) return;
  const component = context.componentLibrary[componentId];
  if (!component) return;
  const nextAncestors = new Set(ancestors);
  nextAncestors.add(componentId);
  collectSceneLayerLayoutMetrics(component.scene, component.scene.rootIds, rootSpace, context, metrics, nextAncestors, pathPrefix, inheritedSerialized, inheritedRuntime);
}

function measureLayerForCore(
  node: LayerNode,
  rootSpace: Space,
  context: SceneLayoutContext,
  overrides: Record<string, unknown>,
): CoreLayerLayoutMetric {
  const runtime = context.resolveRuntime?.(node.layerId) ?? null;
  const intrinsic = runtime
    ? runtime.measure(mergeLayerParams(context.params, overrides), rootSpace)
    : { w: 0, h: 0 };
  return {
    intrinsic: sanitizeSize(intrinsic),
    usesLayoutBounds: runtime ? hasContentAxis(runtime.sizeMode) : false,
  };
}

function mergeLayerParams(base: ParamStore, overrides: Record<string, unknown>): ParamStore {
  return mergeParamOverrides(base, overrides);
}

function sanitizeSize(size: Size): Size {
  return {
    w: Number.isFinite(size.w) ? size.w : 0,
    h: Number.isFinite(size.h) ? size.h : 0,
  };
}

function normalizeCoreLayoutResult(result: CoreLayoutResult): LayoutResult {
  const measureMap: MeasureResultMap = {};
  for (const [nodeId, measure] of coreMapEntries(result.measureMap)) {
    measureMap[nodeId] = measure;
  }

  const layoutMap: LayoutMap = {};
  for (const [nodeId, layout] of coreMapEntries(result.layoutMap)) {
    layoutMap[nodeId] = {
      fromLocal: toMat4(layout.fromLocal),
      toLocal: toMat4(layout.toLocal),
      localBounds: layout.localBounds,
      quad: layout.quad,
      aabb: layout.aabb,
    };
  }
  return {
    measureMap,
    layoutMap,
  };
}

function coreMapEntries<K extends string, V>(map: CoreMap<K, V>): Array<[K, V]> {
  return map instanceof Map
    ? Array.from(map.entries())
    : Object.entries(map) as Array<[K, V]>;
}

function toMat4(value: ArrayLike<number>): Mat4 {
  return new Float64Array(Array.from(value)) as Mat4;
}
